import { useState } from 'react'
import type { LiveShiftPosition } from '../types/LevelData'

interface Props {
  positions: LiveShiftPosition[]
  presets: number[]
  onStart: (positionId: string, goal: number) => void
}

/// Pre-shift picker for live positioned shifts: choose a position to work + an
/// aircraft-worked goal, then start. Mirrors the iOS HandoffSetupView. Shown
/// while the session is in the `preShift` state; the feed holds until "Start".
export function PositionSelect({ positions, presets, onStart }: Props) {
  const [posId, setPosId] = useState(positions[0]?.id ?? '')
  const [goal, setGoal] = useState(presets[1] ?? presets[0] ?? 20)
  const canStart = posId !== ''

  return (
    <div className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
      <div className="w-[min(92vw,28rem)] rounded-2xl bg-slate-900/90 p-6 text-white shadow-2xl ring-1 ring-white/10">
        <h2 className="text-lg font-bold">Start your shift</h2>
        <p className="mt-1 text-sm text-white/60">
          Pick the position you&apos;ll work — traffic at the other airports flies itself.
        </p>

        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-white/50">
            Position
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {positions.map((p) => (
              <button
                key={p.id}
                onClick={() => setPosId(p.id)}
                className={
                  'rounded-lg px-3 py-2 text-sm font-semibold ring-1 transition-colors ' +
                  (posId === p.id
                    ? 'bg-emerald-600 text-white ring-emerald-400'
                    : 'bg-slate-800/70 text-white/80 ring-white/10 hover:bg-slate-700/70')
                }
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-white/50">
            Shift goal — aircraft to work
          </div>
          <div className="mt-2 flex items-center gap-2">
            {presets.map((n) => (
              <button
                key={n}
                onClick={() => setGoal(n)}
                className={
                  'flex-1 rounded-lg px-3 py-2 text-sm font-semibold ring-1 transition-colors ' +
                  (goal === n
                    ? 'bg-emerald-600 text-white ring-emerald-400'
                    : 'bg-slate-800/70 text-white/80 ring-white/10 hover:bg-slate-700/70')
                }
              >
                {n}
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <input
              type="range"
              min={5}
              max={99}
              step={5}
              value={goal}
              onChange={(e) => setGoal(Number(e.target.value))}
              className="flex-1 accent-emerald-500"
              aria-label="Shift goal"
            />
            <span className="w-10 text-right text-sm tabular-nums text-white/80">{goal}</span>
          </div>
        </div>

        <button
          disabled={!canStart}
          onClick={() => onStart(posId, goal)}
          className="mt-6 w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-lg ring-1 ring-emerald-400/50 transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Start shift →
        </button>
      </div>
    </div>
  )
}
